import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useLocation, useHistory } from 'react-router-dom';
import Confetti from 'react-confetti';

import { colorPalette } from 'core/components/theme';
import { withStore } from 'core/store';
import { AuthContext } from 'core/components/auth/auth.context';
import { OfferStatus } from 'core/store/offer-store';
import { Spinner } from 'core/components/spinner';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { prefersReducedMotion } from 'core/helpers/reduced-motion';
import { extractCleanOfferId } from 'offer/helpers/offer-id-helpers';
import SpinTheWheel from './spin-the-wheel';
import useStyles, { CONFETTI_DURATION_MS } from './purchased.styles';

const Purchased = ({ offerId, store: { offer: offerStore } }) => {
  const classes = useStyles();
  const location = useLocation();
  const { isAgency } = useContext(AuthContext);
  const [showConfetti, setShowConfetti] = useState(() => !prefersReducedMotion());

  const cleanedOfferId = extractCleanOfferId(offerId);

  // Load the offer if we don't have it (e.g., page refresh)
  useEffect(() => {
    if (!offerStore.selectedOption && cleanedOfferId) {
      offerStore.getOffer(cleanedOfferId);
    }
  }, [cleanedOfferId, offerStore]);

  useEffect(() => {
    offerStore.pollFetchPolicyFromOffer(cleanedOfferId);
  }, [cleanedOfferId, offerStore]);

  // Stop confetti after 2.5 seconds
  useEffect(() => {
    if (offerStore.status === OfferStatus.FetchSuccessful) {
      const timer = setTimeout(() => setShowConfetti(false), CONFETTI_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [offerStore.status]);

  const history = useHistory();
  const onGoToCustomer = useCallback(() => {
    if (isAgency) {
      // Agency users should open the customer page in a new tab
      window.open(`/customer/${offerStore.purchasedAccountId}`, '_blank');
    } else {
      history.push(`/customer/${offerStore.purchasedAccountId}`);
    }
  }, [isAgency, history, offerStore.purchasedAccountId]);

  const onGoToWelcomeEmail = useCallback(() => {
    window.open(`https://fly.customer.io/env/92447/people/${offerStore.purchasedAccountId}/sent`, '_blank');
  }, [offerStore.purchasedAccountId]);

  const customerEmail = (location.state?.email || offerStore.offer?.quote?.email || '').toLowerCase();

  return (
    <>
      {/* Rendered before the result arrives so screen readers announce it when it does */}
      <span role="status" className={classes.srOnly}>
        {offerStore.status === OfferStatus.FetchSuccessful ? 'Policies are created.' : ''}
      </span>
      {offerStore.status === OfferStatus.FetchingPolicyFromOffer && (
        <Grid container direction="column" justifyContent="center" alignItems="center" className={classes.container}>
          <Label type="title">The purchase is completed!</Label>
          <div className={classes.spinner}>
            <Spinner size={96} />
          </div>
          <Label type="greenSmall">Waiting for the policies to be created...</Label>
        </Grid>
      )}
      {offerStore.status === OfferStatus.FetchSuccessful && (
        <>
          {showConfetti && (
            <div className={classes.confetti} aria-hidden="true">
              <Confetti
                width={window.innerWidth}
                height={window.innerHeight}
                colors={[colorPalette.green_90, colorPalette.green_75, colorPalette.green_75, colorPalette.green_75]}
              />
            </div>
          )}
          <Grid container direction="column" justifyContent="center" alignItems="center" className={classes.container}>
            <Label type="title">The purchase is completed!</Label>
            <Label type="greenSmall" className={classes.subtitle}>
              Policies are created. We sent the application and account credentials to {customerEmail}
            </Label>
            <SpinTheWheel offerId={cleanedOfferId} offerStore={offerStore} />
            <Button variant="contained" color="primary" className={classes.button} onClick={onGoToCustomer}>
              {isAgency && <OpenInNewIcon className={classes.openInNewIcon} />}
              Go to member page
            </Button>
            {!isAgency && (
              <Button variant="contained" color="secondary" className={classes.button} onClick={onGoToWelcomeEmail}>
                Preview welcome email
              </Button>
            )}
          </Grid>
        </>
      )}
      {offerStore.status === OfferStatus.FetchFailed && (
        <Grid container direction="column" justifyContent="center" alignItems="center" className={classes.container}>
          <Label type="title">The purchase is completed!</Label>
          <Label type="greenSmall" className={classes.subtitle}>
            Something is taking longer than expected. We will send the application and account credentials to{' '}
            {customerEmail} when the policies are done being created.
          </Label>
          {/* Only looks the policies up again; the purchase itself is never re-run from here */}
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={() => offerStore.pollFetchPolicyFromOffer(cleanedOfferId)}
          >
            Check status
          </Button>
        </Grid>
      )}
    </>
  );
};

Purchased.propTypes = {
  offerId: PropTypes.string.isRequired,
  store: PropTypes.object.isRequired
};

export default withStore(Purchased);
